import {
  HORIZONTAL,
  NavScroll,
  OnScrollOpt,
  ORIENTATION,
  VERTICAL,
} from '@accedo/virtual-scroll';
import { Nav } from '@accedo/spatial-nav';
import { FunctionalComponent } from 'preact';
import { stylesheet } from 'astroturf';
import clsx from 'clsx';
import { useEffect, useRef } from 'preact/hooks';
import { memo } from 'preact/compat';
import { nav } from '##/init';
import { ComponentTypes } from '##/utils/types';
import { PageNavigation } from '##/utils/PageNavigation';
import {
  ChannelGroup,
  LiveChannel,
} from '##/services/tenApi/collections/types';

import {
  GENRE_SELECTOR_NAV_ID,
  safeAssignFocus,
  SIDE_MENU_NAV_ID,
  isShowPage,
} from '##/utils';
import { isDeviceIQ3 } from '##/utils/device';
import { config } from '../../services/store/store';
import { ShelfTitle } from '../Grid/ShelfTitle';
import { PoweredByBranding } from '../PoweredByBranding';
import { useAsyncValue, useKeyPress } from '../../utils/hooks';
import { LiveGenreSelector } from './GenreSelector';

const isUpNextRoute = (): boolean => window.location.pathname === '/up-next';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .shelf-title {
    margin-bottom: pxToVh(24);
    position: relative;
  }

  .carousel-items-wrapper {
    display: flex;
  }

  .flex-column {
    flex-direction: column;
  }
`;

// Little overlap between different carousel types
// (standard, live, etc) so this is any for now
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TileProps = any;

export interface CarouselData {
  items?: TileProps[];
  Tile: FunctionalComponent<TileProps>;
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onTileClick: (itemData: any, currentPlaying?: any) => void;
  handleFocus?: (arg: boolean) => void;
  scrollEndBoundary: number;
  index: number;
  title?: string;
  carouselTitle?: string;
  lazyLoadBoundary?: number;
  orientation?: ORIENTATION;
  onScroll?: ({ index }: OnScrollOpt) => void;
  type: ComponentTypes;
  isInbound?: boolean;
  imageUrl?: string;
  scrollOffset?: number;
  tileIdsAsNavIds?: boolean;
  liveProgress?: number;
  currentPlaying?: string;
  isPlayerCarousel?: boolean;
  channels?: LiveChannel[];
  groups?: ChannelGroup[];
  images?: {
    brandingImageLightUrl: string;
    brandingImageDarkUrl: string;
  };
  channelGroups?: ChannelGroup[];
}

const LazyTile: FunctionalComponent<CarouselData & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemData: any;
  tileId: string;
  index: number;
  isInbound?: boolean;
  liveProgress?: number;
  currentPlaying?: string;
}> = (props) => {
  const {
    itemData,
    tileId,
    index,
    Tile,
    isInbound: tileIsInbound,
    liveProgress,
    currentPlaying,
    onTileClick,
  } = props;
  const currentPlayingRef = useRef('');

  useEffect(() => {
    if (currentPlaying) {
      currentPlayingRef.current = currentPlaying;
    }
  }, [currentPlaying]);

  const onSelect = (currentPlaying: string | undefined): void => {
    if (itemData.isAddMoreShowsButton) {
      PageNavigation.favouriteMoreShowsPressed$.send();
    }
    if (currentPlaying) {
      onTileClick(itemData, currentPlaying);
    } else {
      onTileClick(itemData);
    }
  };

  return (
    <Nav.Focusable
      id={tileId}
      index={index}
      onSelect={() => onSelect(currentPlayingRef.current)}
    >
      <Tile
        domId={tileId}
        {...itemData}
        liveProgress={liveProgress}
        currentPlaying={currentPlaying}
        isInbound={tileIsInbound !== false}
      />
    </Nav.Focusable>
  );
};

const focusOnGenre = (idx: number): void =>
  safeAssignFocus(`${GENRE_SELECTOR_NAV_ID}-${idx}`);

const getItemIndex = (nodeId = ''): number =>
  parseInt(nodeId?.split('-')?.pop() as string, 10);

const BaseCarouselComponent: FunctionalComponent<CarouselData> = (props) => {
  const {
    items: itemProps,
    id: carouselId,
    title,
    scrollEndBoundary,
    index: baseCarouselIndex,
    orientation = HORIZONTAL,
    onScroll: propsOnScroll,
    lazyLoadBoundary,
    isInbound: carouselIsInbound,
    scrollOffset,
    liveProgress,
    currentPlaying,
    type,
    isPlayerCarousel,
    channels,
    channelGroups,
    images,
  } = props;

  const isIQ3Ref = useAsyncValue(isDeviceIQ3);
  const lastScrollIdx = useRef<number>(0);

  const isLive = type === ComponentTypes.Live;

  useKeyPress(
    'BACK',
    () => {
      const { currentFocusNodeId, currentFocusNode } = nav;

      if (currentFocusNodeId?.includes(GENRE_SELECTOR_NAV_ID)) {
        const groupIdx = getItemIndex(currentFocusNodeId);

        if (!groupIdx) {
          safeAssignFocus(SIDE_MENU_NAV_ID);

          return true;
        }

        focusOnGenre(0);

        if (isIQ3Ref.current) {
          nav.onKeyEvent('ENTER');
        }

        return true;
      }

      if (currentFocusNode?.parent !== carouselId) {
        return false;
      }

      const itemIdx = getItemIndex(currentFocusNodeId);

      if (!itemIdx) {
        safeAssignFocus(SIDE_MENU_NAV_ID);

        return true;
      }

      const { nodeChildren } = nav.getNode(carouselId) || {};

      safeAssignFocus(Object.keys(nodeChildren || {})?.[0]);
      return true;
    },
    !isPlayerCarousel && !isShowPage(window.location.href) && !isUpNextRoute(),
  );

  if (!itemProps?.length && !channels?.length) {
    return null;
  }

  const handleScroll = (scrollData: OnScrollOpt): void => {
    if (lastScrollIdx.current === scrollData.index) {
      return;
    }
    lastScrollIdx.current = scrollData.index;
    propsOnScroll?.(scrollData);
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (carouselIsInbound && lastScrollIdx.current) {
      nav.setActiveChild(
        carouselId,
        `${carouselId}-item-${lastScrollIdx.current}`,
      );
    }
  }, [carouselIsInbound]);

  const { length = 0 } = channelGroups || [];

  const { brandingImageDarkUrl } = images || {};

  const numberOfItemsInCarousel = (itemProps || channels)?.length;

  const { liveTvSettings } = config.$get().app || {};

  const liveBrandingImage =
    brandingImageDarkUrl || liveTvSettings?.brandingImageDarkUrl || '';

  const lastChannelGroupKeysLength =
    channelGroups?.[length - 1]?.keys?.length || 0;

  const customScrollEndBoundary = isLive
    ? Math.min(Math.max(lastChannelGroupKeysLength - 1, 1), 5)
    : scrollEndBoundary;

  return (
    <div
      id={carouselId}
      className={styles.carouselContainer}
      data-scroll-offset={scrollOffset ?? -167}
    >
      {title && type !== ComponentTypes.Featured && (
        <ShelfTitle className={styles.shelfTitle} title={title}>
          {isLive && Boolean(liveBrandingImage) && !isPlayerCarousel && (
            <PoweredByBranding logoUrl={liveBrandingImage} />
          )}
        </ShelfTitle>
      )}
      {isLive && length > 1 && (
        <LiveGenreSelector
          isPlayerCarousel={isPlayerCarousel}
          carouselId={carouselId}
          items={channelGroups || []}
          {...(isLive && { liveShows: channels })}
        />
      )}
      <NavScroll
        id={carouselId}
        index={baseCarouselIndex}
        orientation={orientation}
        scrollEndBoundary={customScrollEndBoundary}
        lazyLoadBoundary={lazyLoadBoundary}
        onScroll={handleScroll}
        animate={false}
        className={clsx(
          styles.carouselItemsWrapper,
          orientation === VERTICAL && styles.flexColumn,
        )}
      >
        {carouselIsInbound !== false ? (
          (itemProps || channels)?.map((itemData, index) => {
            const tileId = `${carouselId}-item-${index}`;

            return (
              <LazyTile
                {...props}
                itemData={{
                  ...itemData,
                  ...(isLive && {
                    carouselInteractionCategory: itemData.genre,
                  }),
                  carouselId,
                  carouselTitle: title,
                  carouselNumberOfItems: numberOfItemsInCarousel,
                  carouselVerticalIndex: baseCarouselIndex,
                  carouselType: type,
                  carouselItemIndex: index,
                }}
                tileId={tileId}
                index={index}
                liveProgress={liveProgress}
                currentPlaying={currentPlaying}
              />
            );
          })
        ) : (
          <></>
        )}
      </NavScroll>
    </div>
  );
};

const BaseCarousel = memo(
  BaseCarouselComponent,
  (prevProps, nextProps) =>
    !(
      prevProps.isInbound !== nextProps.isInbound ||
      prevProps.items?.length !== nextProps.items?.length ||
      prevProps.channels?.length !== nextProps.channels?.length ||
      prevProps.channelGroups?.length !== nextProps.channelGroups?.length ||
      prevProps.liveProgress !== nextProps.liveProgress
    ),
);

export { BaseCarousel };
